import * as React from 'react'

import { Box, Button, NoticeBox, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import RegularAccessModal from '../../Actions/RegularAccessModal'

interface Props {
  employee: Employee
}

const RegularAccessBanner = ({ employee }: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [openRegularAccessModal, toggleRegularAccessModal] = useToggle(false)
  const { loading } = useAsyncData(async () => {
    await policyAuthorizations.authorizePolicies('employee.create', [])
  }, [])

  const canSeeFutureEmployee = users.current.policy.can('employees.seeFutureEmployees')
  const canAddEmployee = policyAuthorizations.for('employee.create')

  if (
    !canSeeFutureEmployee ||
    (currentEmployee && !hasRegularAccess(currentEmployee.hasRegularAccess)) ||
    employee.isTerminated ||
    hasRegularAccess(employee.attributes.has_regular_access)
  ) {
    return null
  }

  return (
    <>
      {loading && <Loading />}
      {!loading && (
        <NoticeBox
          color="grey000White"
          iconColor="grey600"
          border={{
            bottom: {
              style: 'solid',
              width: 's1',
              color: 'grey400',
            },
          }}
          message={
            <Box gap="s8" flexDirection="row">
              <Text.Normal weight="medium">{i18n.t('employees.regular_access_banner')}</Text.Normal>
              <HelpCenterLink name="welcome_space">{i18n.t('employees.more_info')}</HelpCenterLink>
            </Box>
          }
          alignItems="center"
          fullWidth
          component={
            canAddEmployee && (
              <Box paddingLeft="s8">
                <Button onClick={toggleRegularAccessModal(true)}>
                  {i18n.t('employees.regular_access_action')}
                </Button>
              </Box>
            )
          }
        />
      )}
      {openRegularAccessModal && (
        <RegularAccessModal employee={employee} onClose={toggleRegularAccessModal(false)} />
      )}
    </>
  )
}

export default observer(RegularAccessBanner)
